// © 2026 Buddy Media LLC. All rights reserved.
import { useEffect, useMemo, useState } from "react";

import { AdminCard } from "@/components/AdminCard";
import { AdminPageShell } from "@/components/AdminPageShell";
import { ApiError, apiFetch } from "@/lib/api";

// T-153 — Path C v2 merge daemon admin surface.
//
// The daemon runs on buddynet-mcp-server (`buddynet-mcp-server`, post-T-161
// relocation from buddywatch-dev) as a systemd service. It auto-merges
// feature branches per branches-auto-merge-with-rollback:
// deterministic gate (authority + smoke-evidence + diff-size
// threshold), GitHub REST API direct (no gh CLI, no Claude in loop),
// then deploy-cluster.sh + writes deploy_sha to MCP.
//
// This surface is READ-ONLY at v1. The daemon emits its own activity to
// MCP q_stream; the backend reads those events. Override / force-merge
// / skip-task UX defers to v2 (Marc design input needed per T-153 stop
// signal).
//
// Endpoints:
//   GET /api/admin/merge-daemon/status   — last heartbeat + status band
//   GET /api/admin/merge-daemon/recent   — recent merge attempts
//   GET /api/admin/merge-daemon/blocked  — currently-blocked branches

type StatusBand = "alive" | "warn" | "stale" | "no_heartbeat_recorded";

type DaemonStatus = {
  generated_at: string;
  status: StatusBand;
  last_heartbeat_at: string | null;
  silence_seconds: number | null;
  uptime_seconds: number | null;
  tick_count: number | null;
  last_tick_result: string | null;
  host: string | null;
  poll_interval_s: number | null;
  max_diff_lines: number | null;
};

type AttemptEvent = {
  id: string;
  ts: string;
  event_type:
    | "merge_completed"
    | "merge_blocked"
    | "deploy_failed_after_merge"
    | "mcp_update_failed_after_deploy";
  actor: string;
  task_id: string | null;
  message: string;
  metadata: Record<string, unknown>;
};

const STATUS_PILL: Record<StatusBand, string> = {
  alive: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300",
  warn: "border-amber-500/40 bg-amber-500/15 text-amber-300",
  stale: "border-rose-500/40 bg-rose-500/15 text-rose-300",
  no_heartbeat_recorded: "border-slate-600 bg-slate-800 text-slate-400",
};

const STATUS_LABEL: Record<StatusBand, string> = {
  alive: "Alive",
  warn: "Warn (silent)",
  stale: "Stale (likely down)",
  no_heartbeat_recorded: "Never reported",
};

const ATTEMPT_PILL: Record<AttemptEvent["event_type"], { label: string; color: string }> = {
  merge_completed: { label: "Merged + deployed", color: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" },
  merge_blocked: { label: "Blocked", color: "border-amber-500/40 bg-amber-500/15 text-amber-300" },
  deploy_failed_after_merge: { label: "Deploy failed", color: "border-rose-500/40 bg-rose-500/15 text-rose-300" },
  mcp_update_failed_after_deploy: { label: "MCP update failed", color: "border-rose-500/40 bg-rose-500/15 text-rose-300" },
};

function fmtTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtDuration(secs: number | null): string {
  if (secs === null || secs === undefined) return "—";
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}m`;
  if (secs < 86400) return `${(secs / 3600).toFixed(1)}h`;
  return `${(secs / 86400).toFixed(1)}d`;
}

function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = (err.data as { detail?: string } | undefined)?.detail;
    return `${fallback} (HTTP ${err.status})${detail ? `: ${detail}` : ""}`;
  }
  return fallback;
}

export function MergeDaemonPage() {
  const [status, setStatus] = useState<DaemonStatus | null>(null);
  const [recent, setRecent] = useState<AttemptEvent[] | null>(null);
  const [blocked, setBlocked] = useState<AttemptEvent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      apiFetch<DaemonStatus>("/api/admin/merge-daemon/status"),
      apiFetch<AttemptEvent[]>("/api/admin/merge-daemon/recent?limit=50"),
      apiFetch<AttemptEvent[]>("/api/admin/merge-daemon/blocked"),
    ])
      .then(([s, r, b]) => {
        if (cancelled) return;
        setStatus(s);
        setRecent(r);
        setBlocked(b);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(describeError(err, "Failed to load merge-daemon data"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const blockedReasons = useMemo(() => {
    if (!blocked) return [] as Array<{ reason: string; count: number }>;
    const counts = new Map<string, number>();
    for (const ev of blocked) {
      // Reason is the first colon-prefixed token in the message, e.g.
      // "gate_failed: smoke_evidence_missing branch=feature/x".
      const m = ev.message.match(/(?:gate_failed:\s*)?([a-z_]+)/);
      const reason = m ? m[1] : "other";
      counts.set(reason, (counts.get(reason) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count);
  }, [blocked]);

  return (
    <AdminPageShell
      title="Merge daemon"
      description="Path C v2 — bash daemon on buddynet-mcp-server (post-T-161 relocation from buddywatch-dev) that auto-merges feature branches per branches-auto-merge-with-rollback. Deterministic gate (authority + smoke-evidence + diff-size threshold), GitHub REST API direct, deploy-cluster.sh, writes deploy_sha to MCP. NO Claude in the loop. Read-only v1; override / force-merge / skip-task UX defers to v2 with Marc design input."
    >
      <div className="space-y-4">
        {/* T-156: cross-link to where the daemon's bearer is rotated. */}
        <AdminCard className="border-emerald-900/40 bg-emerald-950/10">
          <p className="text-xs text-slate-300">
            <span className="font-semibold text-emerald-300">Bearer rotation:</span>{" "}
            the daemon's MCP bearer is admin-managed at{" "}
            <a
              href="/admin/operations/secrets"
              className="text-pink-400 hover:underline"
            >
              /admin/operations/secrets
            </a>
            {" "}→ <em>Merge daemon MCP bearer</em> row → <em>Mint + install</em>.
            Per Rule J (<code>secrets-only-via-admin-stores</code>), the bearer
            is fetched per-tick from an encrypted slot — never pasted into an
            env file. Rotations are picked up within ≤60s without restarting
            the daemon.
          </p>
        </AdminCard>

        {error ? (
          <AdminCard className="border-rose-900/40 bg-rose-950/20">
            <p className="text-sm text-rose-200">{error}</p>
          </AdminCard>
        ) : null}

        {/* Status header */}
        <AdminCard className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-slate-200">Daemon status</h3>
              <p className="text-[11px] text-slate-500">
                Generated at {fmtTime(status?.generated_at ?? null)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setRefreshKey((k) => k + 1)}
              disabled={loading}
              className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {status ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Status</p>
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_PILL[status.status]}`}
                >
                  {STATUS_LABEL[status.status]}
                </span>
              </div>
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Last heartbeat</p>
                <p className="font-mono text-xs text-slate-200">
                  {fmtTime(status.last_heartbeat_at)}
                </p>
                {status.silence_seconds !== null ? (
                  <p className="text-[11px] text-slate-500">
                    {fmtDuration(status.silence_seconds)} ago
                  </p>
                ) : null}
              </div>
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Daemon uptime</p>
                <p className="font-mono text-xs text-slate-200">
                  {fmtDuration(status.uptime_seconds)}
                </p>
                {status.tick_count !== null ? (
                  <p className="text-[11px] text-slate-500">
                    {status.tick_count.toLocaleString()} ticks
                  </p>
                ) : null}
              </div>
              <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Host</p>
                <p className="font-mono text-xs text-slate-200">
                  {status.host ?? "—"}
                </p>
                {status.poll_interval_s !== null ? (
                  <p className="text-[11px] text-slate-500">
                    poll {status.poll_interval_s}s · diff cap {status.max_diff_lines}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {status?.last_tick_result ? (
            <p className="font-mono text-[11px] text-slate-400">
              last tick: {status.last_tick_result}
            </p>
          ) : null}

          {status?.status === "no_heartbeat_recorded" ? (
            <p className="text-xs text-amber-300">
              No heartbeat recorded yet. The daemon may not be installed or
              started. See{" "}
              <code className="rounded bg-slate-800 px-1 py-0.5 text-[11px]">
                docs/items/merge-daemon-bootstrap-2026-05-04.md
              </code>{" "}
              for the one-time install runbook (originally buddywatch-dev; relocated to buddynet-mcp-server post-T-161).
            </p>
          ) : null}
        </AdminCard>

        {/* Currently blocked */}
        <AdminCard className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-slate-200">
              Currently blocked
              {blocked ? (
                <span className="ml-2 text-xs font-normal text-slate-400">
                  ({blocked.length})
                </span>
              ) : null}
            </h3>
            {blockedReasons.length > 0 ? (
              <div className="flex flex-wrap gap-2 text-[11px]">
                {blockedReasons.map(({ reason, count }) => (
                  <span
                    key={reason}
                    className="rounded-full border border-slate-700 bg-slate-900 px-2 py-0.5 font-mono text-slate-300"
                  >
                    {reason} · {count}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          {blocked === null ? (
            <p className="text-xs text-slate-500">Loading…</p>
          ) : blocked.length === 0 ? (
            <p className="text-xs text-slate-400">
              No tasks currently blocked. The daemon either has nothing to merge
              or every candidate is en route through the gate.
            </p>
          ) : (
            <ul className="space-y-2">
              {blocked.map((ev) => (
                <li
                  key={ev.id}
                  className="rounded-md border border-amber-900/40 bg-amber-950/20 p-3 text-xs"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-amber-200">
                      {ev.task_id ?? "—"}
                    </span>
                    <span className="font-mono text-slate-500">{fmtTime(ev.ts)}</span>
                  </div>
                  <p className="mt-1 font-mono text-amber-100">{ev.message}</p>
                </li>
              ))}
            </ul>
          )}
        </AdminCard>

        {/* Recent attempts */}
        <AdminCard className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-200">
            Recent attempts
            {recent ? (
              <span className="ml-2 text-xs font-normal text-slate-400">
                (last {recent.length})
              </span>
            ) : null}
          </h3>

          {recent === null ? (
            <p className="text-xs text-slate-500">Loading…</p>
          ) : recent.length === 0 ? (
            <p className="text-xs text-slate-400">
              No daemon activity yet. Daemon may not be installed or no
              candidate tasks have been processed.
            </p>
          ) : (
            <div className="overflow-hidden rounded-md border border-slate-800">
              <table className="min-w-full divide-y divide-slate-800 text-xs">
                <thead className="bg-slate-900/60">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      When
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      Outcome
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      Task
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      Detail
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {recent.map((ev) => {
                    const pill = ATTEMPT_PILL[ev.event_type];
                    return (
                      <tr key={ev.id} className="hover:bg-slate-900/40">
                        <td className="px-3 py-2 font-mono text-slate-300">
                          {fmtTime(ev.ts)}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${pill.color}`}
                          >
                            {pill.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-300">
                          {ev.task_id ?? "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-slate-400">
                          {ev.message}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </AdminCard>
      </div>
    </AdminPageShell>
  );
}
